import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, LogOut, Mail, RefreshCw } from "lucide-react";
import { Logo } from "@/components/site/Primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "lm_admin_token";

const errMsg = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg).join(" ") : "Errore. Riprova.");
const fmt = (iso) => new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
const KIND = { social: "Feed social", web: "Sito web" };
const SERVICE = { social: "Social media", web: "Sito web", both: "Entrambi" };

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      setError(errMsg(err.response?.data?.detail));
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen grid place-items-center px-5">
      <form onSubmit={submit} className="w-full max-w-sm rounded-[28px] border border-[var(--line)] bg-[var(--card)] p-8 space-y-6" data-testid="admin-login-form">
        <Logo className="w-11 h-11" />
        <h1 className="font-display text-4xl">Area riservata</h1>
        <label className="block"><span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">Email</span>
          <input data-testid="admin-email-input" type="email" className="field" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
        <label className="block"><span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">Password</span>
          <input data-testid="admin-password-input" type="password" className="field" value={password} onChange={(e) => setPassword(e.target.value)} /></label>
        {error && <p className="text-sm text-rose-400" data-testid="admin-login-error">{error}</p>}
        <button data-testid="admin-login-btn" disabled={loading} className="btn-gold w-full justify-center disabled:opacity-60">
          {loading ? <Loader2 size={18} className="animate-spin" /> : "Accedi"}
        </button>
      </form>
    </div>
  );
};

const Table = ({ rows, cols, testid }) => (
  <div className="overflow-x-auto rounded-[20px] border border-[var(--line)]" data-testid={testid}>
    <table className="w-full text-sm min-w-[720px]">
      <thead><tr className="text-left font-mono text-[11px] uppercase tracking-[0.15em] text-stone-400 border-b border-[var(--line)]">
        {cols.map((c) => <th key={c.k} className="px-4 py-3 font-normal">{c.l}</th>)}
      </tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={cols.length} className="px-4 py-10 text-center text-stone-400">Nessuna richiesta per ora.</td></tr>}
        {rows.map((r) => (
          <tr key={r.id} className="border-b border-[var(--line)] last:border-0 align-top hover:bg-white/[0.02]">
            {cols.map((c) => <td key={c.k} className="px-4 py-4 text-stone-200">{c.r ? c.r(r) : r[c.k]}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const mail = (r) => (
  <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1.5 text-[var(--gold)] hover:underline"><Mail size={14} />{r.email}</a>
);

const Dashboard = ({ token, onLogout }) => {
  const [tab, setTab] = useState("leads");
  const [data, setData] = useState({ leads: [], contacts: [], bookings: [] });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const h = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const [l, c, b] = await Promise.all([axios.get(`${API}/admin/leads`, h), axios.get(`${API}/admin/contacts`, h), axios.get(`${API}/admin/bookings`, h)]);
      const arr = (r) => (Array.isArray(r.data) ? r.data : []);
      setData({ leads: arr(l), contacts: arr(c), bookings: arr(b) });
    } catch (e) {
      if (e.response?.status === 401) onLogout();
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tabs = [
    { id: "leads", l: "Mockup richiesti", n: data.leads.length },
    { id: "contacts", l: "Messaggi contatto", n: data.contacts.length },
    { id: "bookings", l: "Chiamate prenotate", n: data.bookings.length },
  ];
  const bookingCols = [
    { k: "date", l: "Giorno", r: (r) => <span className="font-mono">{r.date.split("-").reverse().join("/")} · {r.time}</span> },
    { k: "name", l: "Nome" },
    { k: "email", l: "Email", r: mail },
    { k: "mode", l: "Modalità", r: (r) => (r.mode === "phone" ? `Telefono ${r.phone}` : "Videochiamata") },
    { k: "note", l: "Note", r: (r) => r.note || "—" },
  ];
  const leadCols = [
    { k: "created_at", l: "Data", r: (r) => fmt(r.created_at) },
    { k: "name", l: "Nome" },
    { k: "email", l: "Email", r: mail },
    { k: "business", l: "Attività / profilo", r: (r) => r.business || "—" },
    { k: "kind", l: "Richiesta", r: (r) => KIND[r.kind] ?? r.kind },
    { k: "lang", l: "Lingua", r: (r) => r.lang?.toUpperCase() },
  ];
  const contactCols = [
    { k: "created_at", l: "Data", r: (r) => fmt(r.created_at) },
    { k: "name", l: "Nome" },
    { k: "email", l: "Email", r: mail },
    { k: "service", l: "Servizio", r: (r) => SERVICE[r.service] ?? r.service },
    { k: "message", l: "Messaggio", r: (r) => <p className="max-w-md whitespace-pre-wrap text-stone-300">{r.message}</p> },
  ];

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3"><Logo /><span className="font-display text-2xl">Area riservata</span></div>
        <div className="flex gap-3">
          <button onClick={load} data-testid="admin-refresh-btn" className="btn-ghost !py-2.5 text-sm"><RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Aggiorna</button>
          <button onClick={onLogout} data-testid="admin-logout-btn" className="btn-ghost !py-2.5 text-sm"><LogOut size={16} /> Esci</button>
        </div>
      </header>
      <h1 className="mt-12 font-display font-light text-4xl sm:text-5xl">Contatti raccolti</h1>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
            className={`text-left rounded-[20px] border p-5 transition-colors ${tab === t.id ? "border-[var(--gold)] bg-[#2a2219]" : "border-[var(--line)] bg-[var(--card)] hover:border-stone-500"}`}>
            <p className="font-display text-5xl" data-testid={`admin-count-${t.id}`}>{t.n}</p>
            <p className="mt-1 text-sm text-stone-300">{t.l}</p>
          </button>
        ))}
      </div>
      <div className="mt-10">
        {tab === "leads" && <Table rows={data.leads} cols={leadCols} testid="admin-leads-table" />}
        {tab === "contacts" && <Table rows={data.contacts} cols={contactCols} testid="admin-contacts-table" />}
        {tab === "bookings" && <Table rows={data.bookings} cols={bookingCols} testid="admin-bookings-table" />}
      </div>
    </div>
  );
};

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(KEY));

  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => { m.remove(); };
  }, []);

  const logout = () => { localStorage.removeItem(KEY); setToken(null); };

  return (
    <div className="site">
      {token ? <Dashboard token={token} onLogout={logout} /> : <Login onLogin={setToken} />}
    </div>
  );
}
